import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { completeOnboarding, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { WiseAceLogo } from "@/components/WiseAceLogo";
import { toast } from "sonner";
import { Building2, Landmark, ArrowRight, Loader2 } from "lucide-react";

const empty = {
  company_name: "", email: "", cell: "", whatsapp: "", address: "",
  vat_number: "", bank_name: "", account_name: "", account_number: "", branch_code: "",
};

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ ...empty, company_name: user?.name || "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  if (user?.workspace_role === "field_employee") return <Navigate to="/field" replace />;
  if (user && (!user.is_owner || user.onboarding_completed === true)) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.company_name.trim()) return toast.error("Please enter your business name.");
    if (!f.email.trim()) return toast.error("Please enter your business email.");
    if (!f.cell.trim()) return toast.error("Please enter a contact number.");
    if (!f.address.trim()) return toast.error("Please enter your business address.");
    setSaving(true);
    try {
      await completeOnboarding(f);
      await refresh();
      toast.success("Business profile saved. Welcome aboard!");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5EF] py-10 px-4" data-testid="onboarding-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex flex-col items-center text-center mb-8">
          <WiseAceLogo size={48} />
          <h1 className="mt-5 font-display text-3xl sm:text-4xl font-bold text-[#0B132B]">
            Set up your business
          </h1>
          <p className="mt-2 text-slate-500 max-w-md">
            These details appear on your invoices, quotes and documents. Enter your own
            business information &mdash; this is unique to your account.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="w-9 h-9 rounded-lg bg-[#0B132B] text-[#D4AF37] flex items-center justify-center"><Building2 className="w-4 h-4" /></span>
              <h3 className="font-display font-semibold text-[#0B132B]">Business Details</h3>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Business Name <span className="text-rose-500">*</span></Label>
                <Input value={f.company_name} onChange={set("company_name")} placeholder="e.g. Acme Plumbing (Pty) Ltd" className="mt-1.5" data-testid="onboarding-company-name" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Business Email <span className="text-rose-500">*</span></Label><Input type="email" value={f.email} onChange={set("email")} placeholder="you@yourbusiness.co.za" className="mt-1.5" data-testid="onboarding-email" /></div>
                <div><Label>Contact Number <span className="text-rose-500">*</span></Label><Input value={f.cell} onChange={set("cell")} placeholder="082 123 4567" className="mt-1.5" data-testid="onboarding-cell" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>WhatsApp (optional)</Label><Input value={f.whatsapp} onChange={set("whatsapp")} className="mt-1.5" data-testid="onboarding-whatsapp" /></div>
                <div><Label>VAT Number (optional)</Label><Input value={f.vat_number} onChange={set("vat_number")} className="mt-1.5" data-testid="onboarding-vat" /></div>
              </div>
              <div>
                <Label>Business Address <span className="text-rose-500">*</span></Label>
                <Textarea value={f.address} onChange={set("address")} rows={2} placeholder="Street, suburb, city, postal code" className="mt-1.5" data-testid="onboarding-address" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="w-9 h-9 rounded-lg bg-[#F1F5F9] text-[#0B132B] flex items-center justify-center"><Landmark className="w-4 h-4" /></span>
              <h3 className="font-display font-semibold text-[#0B132B]">Banking Details <span className="text-xs font-normal text-slate-400">(optional)</span></h3>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Bank Name</Label><Input value={f.bank_name} onChange={set("bank_name")} className="mt-1.5" data-testid="onboarding-bank" /></div>
                <div><Label>Account Name</Label><Input value={f.account_name} onChange={set("account_name")} className="mt-1.5" data-testid="onboarding-account-name" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Account Number</Label><Input value={f.account_number} onChange={set("account_number")} className="mt-1.5" data-testid="onboarding-account-number" /></div>
                <div><Label>Branch Code</Label><Input value={f.branch_code} onChange={set("branch_code")} className="mt-1.5" data-testid="onboarding-branch" /></div>
              </div>
            </div>
          </div>

          <Button type="submit" disabled={saving} className="w-full h-12 bg-[#0B132B] hover:bg-[#1C2541] text-base" data-testid="onboarding-submit">
            {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
            {saving ? "Saving…" : "Continue to Dashboard"}
            {!saving && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
          <p className="text-center text-xs text-slate-400">You can edit all of this later in Business Settings.</p>
        </form>
      </div>
    </div>
  );
}
